import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { SearchableEntitySelector } from '../../components/SearchableEntitySelector';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { KeyboardSafeForm } from '../../components/KeyboardSafeForm';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  RequestFormDateField,
  RequestFormLabel,
  RequestFormTextField,
} from '../../components/RequestFormField';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnderlineTabs } from '../../components/UnderlineTabs';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { normalizeApiError } from '../../api/apiError';
import { getWfhModuleCapabilities } from '../../config/wfhCapabilities';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  getWfhRequests,
  applyWfhRequest,
  getWfhSessionTypes,
  getWfhTeamMembers,
} from '../../services/wfhService';
import type { WfhTeamMember } from '../../types/wfh';
import { ROLE_TEAM_LEAD } from '../../utils/approvalRoles';
import type { LeaveDurationOption, LeaveSessionOption } from '../../types/leave';
import type { WfhRequestListItem } from '../../types/wfh';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import {
  canSeeRequestApprovals,
  wfhApprovalListTitle,
} from '../../utils/approvalRoles';
import {
  formatAttendanceWeekday,
  formatCalendarDate,
} from '../../utils/dateTime';
import { wfhErrorMessage } from '../../utils/wfhErrors';
import { getWfhStatusCategory } from '../../utils/wfhStatus';
import { isValidLeaveDate, validateLeaveDateRange } from '../../utils/leaveFilters';
import {
  formatWfhDurationLabel,
  wfhAdvanceNoticeMessage,
  wfhAdvanceNoticeViolation,
} from '../../utils/wfhSession';
import { localTodayDate } from '../../utils/leaveSession';

type Props = NativeStackScreenProps<RootStackParamList, 'WfhHome'>;
type WfhTab = 'My WFH' | 'Apply WFH';

const WFH_TABS: readonly WfhTab[] = ['My WFH', 'Apply WFH'];

function statusTextColor(statusCode: string | null, palette: ColorPalette) {
  const category = getWfhStatusCategory(statusCode);
  if (category === 'success') return palette.success;
  if (category === 'danger') return palette.error;
  if (category === 'warning') return palette.warning;
  if (category === 'info') return palette.info;
  return palette.textSecondary;
}

function RequestRow({
  item,
  onPress,
  palette,
}: {
  item: WfhRequestListItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const dateLabel = formatCalendarDate(item.fromDate);
  const weekday = formatAttendanceWeekday(item.fromDate) || '—';
  const duration = formatWfhDurationLabel(item);

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${dateLabel}, ${duration ? `${duration}, ` : ''}${item.statusLabel}`}
      onPress={onPress}
      style={[styles.row, { borderBottomColor: palette.border }]}
    >
      <View style={styles.rowLeft}>
        <Text style={[styles.rowTitle, { color: palette.textPrimary }]}>
          {dateLabel}
        </Text>
        <Text style={[styles.rowMeta, { color: palette.textSecondary }]}>
          {duration ? `${weekday} · ${duration}` : weekday}
        </Text>
      </View>
      <View style={styles.rowRight}>
        <Text
          style={[
            styles.statusText,
            { color: statusTextColor(item.statusCode, palette) },
          ]}
        >
          {item.statusLabel}
        </Text>
        <Text style={[styles.rowMeta, { color: palette.textPrimary }]}>
          Work From Home
        </Text>
      </View>
    </Pressable>
  );
}

function teamMemberLabel(member: WfhTeamMember): string {
  return (
    member.displayName ||
    member.employeeNumber ||
    `Employee ${member.employeeId}`
  );
}

function ApplyWfhForm({
  canApply,
  canApplyForTeam,
  palette,
  onSuccess,
}: {
  canApply: boolean;
  canApplyForTeam: boolean;
  palette: ColorPalette;
  onSuccess: (appliedForTeamMember: boolean) => void;
}) {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [workLocation, setWorkLocation] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [durations, setDurations] = useState<LeaveDurationOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [notEligible, setNotEligible] = useState(false);
  const [policyName, setPolicyName] = useState<string | null>(null);
  const [minimumNoticeDays, setMinimumNoticeDays] = useState<number | null>(
    null,
  );
  const [serverDate, setServerDate] = useState<string | null>(null);
  const [durationCode, setDurationCode] = useState('');
  const [sessionCode, setSessionCode] = useState('');
  const [selectedMember, setSelectedMember] = useState<WfhTeamMember | null>(
    null,
  );
  const [submittedFor, setSubmittedFor] = useState<string | null>(null);
  const [advanceNoticeWaived, setAdvanceNoticeWaived] = useState(false);
  const mountedRef = useRef(true);
  const searchResultsRef = useRef<WfhTeamMember[]>([]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const applyingForTeamMember = selectedMember != null;

  const clearTeamMember = () => {
    setSelectedMember(null);
    setDurationCode('');
    setSessionCode('');
    setSubmittedFor(null);
    setSubmitError(null);
  };

  const loadTeamMemberOptions = useCallback(async (search: string) => {
    const items = await getWfhTeamMembers(search);
    searchResultsRef.current = items;
    return items.map(member => ({
      id: member.employeeId,
      label: teamMemberLabel(member),
      subtitle:
        [member.employeeNumber, member.designationName]
          .filter(Boolean)
          .join(' · ') || null,
    }));
  }, []);

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const options = await getWfhSessionTypes(
        null,
        applyingForTeamMember ? selectedMember?.employeeId : null,
      );
      if (!mountedRef.current) return;
      setAdvanceNoticeWaived(options.advanceNoticeWaived);
      setDurations(options.durations);
      setPolicyName(options.policyName);
      setMinimumNoticeDays(options.minimumNoticeDays);
      setServerDate(options.serverDate);
      setOptionsError(null);
      setNotEligible(false);
    } catch (error) {
      if (!mountedRef.current) return;
      setDurations([]);
      setPolicyName(null);
      setMinimumNoticeDays(null);
      setServerDate(null);
      setAdvanceNoticeWaived(false);
      const code = normalizeApiError(error).code;
      setNotEligible(
        code === 'WFH_NOT_ELIGIBLE' || code === 'WFH_EMPLOYEE_NOT_ELIGIBLE',
      );
      setOptionsError(
        wfhErrorMessage(error, 'Unable to load WFH duration options.'),
      );
    } finally {
      if (mountedRef.current) setOptionsLoading(false);
    }
  }, [applyingForTeamMember, selectedMember?.employeeId]);

  useEffect(() => {
    loadOptions().catch(() => undefined);
  }, [loadOptions]);

  const selectedDuration =
    durations.find(option => option.durationCode === durationCode) ??
    durations[0] ??
    null;
  const selectedSession: LeaveSessionOption | null =
    selectedDuration?.sessions.find(
      session => session.sessionCode === sessionCode,
    ) ??
    selectedDuration?.sessions[0] ??
    null;
  const isHalfDay = selectedDuration?.isHalfDay === true;

  const selectDuration = (next: LeaveDurationOption) => {
    if (!canApply) return;
    setDurationCode(next.durationCode);
    setSessionCode(next.sessions[0]?.sessionCode ?? '');
    setSubmitError(null);
  };

  const selectSession = (next: LeaveSessionOption) => {
    if (!canApply || !isHalfDay) return;
    setSessionCode(next.sessionCode);
    setSubmitError(null);
  };

  const submit = async () => {
    if (!canApply || submitting) return;
    if (!selectedDuration || !selectedSession) {
      setSubmitError('Select a WFH duration from the configured options.');
      return;
    }
    const trimmedFrom = fromDate.trim();
    const trimmedTo = isHalfDay ? trimmedFrom : toDate.trim();
    if (isHalfDay && !isValidLeaveDate(trimmedFrom)) {
      setSubmitError('Select a valid date.');
      return;
    }
    const rangeError = validateLeaveDateRange(trimmedFrom, trimmedTo);
    if (rangeError) {
      setSubmitError(rangeError);
      return;
    }
    const noticeWaived = applyingForTeamMember && advanceNoticeWaived;
    if (!noticeWaived) {
      const noticeError = wfhAdvanceNoticeViolation({
        minimumNoticeDays,
        fromDate: trimmedFrom,
        today: serverDate ?? localTodayDate(),
      });
      if (noticeError) {
        setSubmitError(noticeError);
        return;
      }
    }
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setSubmitError('Reason is required.');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      await applyWfhRequest({
        fromDate: trimmedFrom,
        toDate: trimmedTo,
        sessionType: selectedSession.sessionCode,
        reason: trimmedReason,
        workLocation: workLocation.trim() || undefined,
        employeeId: noticeWaived ? selectedMember?.employeeId : undefined,
      });
      setFromDate('');
      setToDate('');
      setWorkLocation('');
      setReason('');
      if (noticeWaived && selectedMember) {
        setSubmittedFor(teamMemberLabel(selectedMember));
        onSuccess(true);
      } else {
        setSubmittedFor(null);
        onSuccess(false);
      }
    } catch (error) {
      setSubmitError(
        wfhErrorMessage(error, 'Unable to submit WFH request.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderChip = (
    key: string,
    label: string,
    selected: boolean,
    onPress: () => void,
  ) => (
    <Pressable
      key={key}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected }}
      onPress={onPress}
      disabled={!canApply}
      style={[
        styles.optionChip,
        {
          borderColor: selected ? palette.primary : palette.border,
          backgroundColor: palette.surface,
        },
      ]}
    >
      <Text
        style={[
          styles.optionChipLabel,
          { color: selected ? palette.primary : palette.textPrimary },
        ]}
      >
        {label}
      </Text>
    </Pressable>
  );

  const canSubmit =
    canApply &&
    !notEligible &&
    !optionsLoading &&
    !!selectedDuration &&
    !!selectedSession;

  return (
    <KeyboardSafeForm>
      {canApplyForTeam ? (
        <View>
          <RequestFormLabel label="Apply for" />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Apply for myself"
            accessibilityState={{ selected: !applyingForTeamMember }}
            disabled={!canApply}
            onPress={clearTeamMember}
            style={[
              styles.memberOption,
              {
                borderColor: !applyingForTeamMember
                  ? palette.primary
                  : palette.border,
              },
            ]}
          >
            <Text style={[styles.optionChipLabel, { color: palette.textPrimary }]}>
              Myself
            </Text>
          </Pressable>
          <SearchableEntitySelector
            label="Team member"
            selectedValue={selectedMember?.employeeId ?? null}
            selectedLabel={
              selectedMember ? teamMemberLabel(selectedMember) : null
            }
            loadOptions={loadTeamMemberOptions}
            minSearchLength={3}
            disabled={!canApply}
            placeholder="Enter at least 3 characters of the employee name"
            onSelect={option => {
              const member = searchResultsRef.current.find(
                item => item.employeeId === option.id,
              ) ?? {
                employeeId: option.id,
                displayName: option.label,
                employeeNumber: null,
                departmentName: null,
                designationName: null,
              };
              setSelectedMember(member);
              setDurationCode('');
              setSessionCode('');
              setSubmittedFor(null);
              setSubmitError(null);
            }}
            onClear={clearTeamMember}
          />
        </View>
      ) : null}
      {policyName ? (
        <Text style={[styles.warning, { color: palette.textMuted }]}>
          WFH policy: {policyName}
        </Text>
      ) : null}
      {applyingForTeamMember && advanceNoticeWaived ? (
        <Text style={[styles.warning, { color: palette.textMuted }]}>
          Advance notice does not apply when you submit WFH for an assigned
          team member. The request stays pending until it is approved.
        </Text>
      ) : minimumNoticeDays != null && minimumNoticeDays > 0 ? (
        <Text style={[styles.warning, { color: palette.textMuted }]}>
          {wfhAdvanceNoticeMessage(minimumNoticeDays)}
        </Text>
      ) : null}
      {submittedFor ? (
        <Text style={[styles.warning, { color: palette.success }]}>
          {`WFH request submitted for ${submittedFor}. It is pending approval.`}
        </Text>
      ) : null}
      <View>
        <RequestFormLabel label="Duration" required />
        {optionsLoading ? (
          <Text style={[styles.warning, { color: palette.textMuted }]}>
            Loading duration options...
          </Text>
        ) : notEligible ? (
          <Text style={[styles.warning, { color: palette.error }]}>
            {optionsError}
          </Text>
        ) : optionsError ? (
          <Pressable
            accessibilityRole="button"
            onPress={() => loadOptions().catch(() => undefined)}
          >
            <Text style={[styles.warning, { color: palette.error }]}>
              {optionsError} Tap to retry.
            </Text>
          </Pressable>
        ) : durations.length === 0 ? (
          <Text style={[styles.warning, { color: palette.error }]}>
            No WFH duration is configured for your WFH policy.
          </Text>
        ) : (
          <View style={styles.optionRow}>
            {durations.map(option =>
              renderChip(
                option.durationCode,
                formatDisplayLabel(option.durationName),
                option.durationCode === selectedDuration?.durationCode,
                () => selectDuration(option),
              ),
            )}
          </View>
        )}
      </View>
      {isHalfDay && selectedDuration ? (
        <View>
          <RequestFormLabel label="Session" required />
          <View style={styles.optionRow}>
            {selectedDuration.sessions.map(option =>
              renderChip(
                option.sessionCode,
                formatDisplayLabel(option.sessionName),
                option.sessionCode === selectedSession?.sessionCode,
                () => selectSession(option),
              ),
            )}
          </View>
        </View>
      ) : null}
      <RequestFormDateField
        label={isHalfDay ? 'Date' : 'From Date'}
        required
        value={fromDate}
        onChange={value => {
          setFromDate(value);
          setSubmitError(null);
        }}
        editable={canApply}
      />
      {isHalfDay ? null : (
        <RequestFormDateField
          label="To Date"
          required
          value={toDate}
          onChange={value => {
            setToDate(value);
            setSubmitError(null);
          }}
          editable={canApply}
        />
      )}
      <RequestFormTextField
        label="Work Location"
        value={workLocation}
        onChangeText={value => {
          setWorkLocation(value);
          setSubmitError(null);
        }}
        placeholder="work location"
        editable={canApply}
      />
      <RequestFormTextField
        label="Reason"
        required
        value={reason}
        onChangeText={value => {
          setReason(value);
          setSubmitError(null);
        }}
        placeholder="Enter reason for working from home"
        multiline
        editable={canApply}
      />
      {submitError ? (
        <Text accessibilityRole="alert" style={[styles.warning, { color: palette.error }]}>
          {submitError}
        </Text>
      ) : null}
      {canSubmit ? (
        <AppButton
          title="Submit Request"
          onPress={submit}
          loading={submitting}
          disabled={submitting}
        />
      ) : (
        <AppButton
          title="Submit Request"
          disabled
          onPress={() => undefined}
        />
      )}
    </KeyboardSafeForm>
  );
}

export function WfhHomeScreen({ navigation }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getWfhModuleCapabilities(auth.permissions);
  const showTeamApprovals = canSeeRequestApprovals(
    auth.roles,
    auth.permissions,
    PERMISSIONS.WFH_APPROVE,
  );
  const canApplyForTeam = auth.hasRole(ROLE_TEAM_LEAD);

  const [activeTab, setActiveTab] = useState<WfhTab>('My WFH');
  const [items, setItems] = useState<WfhRequestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const openDetails = useCallback(
    (item: WfhRequestListItem) => {
      navigation.navigate(routes.wfhDetails, {
        wfhRequestId: item.wfhRequestId,
      });
    },
    [navigation],
  );

  const load = useCallback(
    async (isRefresh = false) => {
      if (!capabilities.canViewHistory) {
        setItems([]);
        setHistoryError(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const result = await getWfhRequests({ page: 1, pageSize: 20 });
        setItems(result.items);
        setHistoryError(null);
      } catch (error) {
        setItems([]);
        setHistoryError(
          wfhErrorMessage(error, 'Unable to load WFH requests.'),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [capabilities.canViewHistory],
  );

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const header = useMemo(
    () => (
      <ScreenHeader
        bleed={false}
        title={activeTab === 'Apply WFH' ? 'Apply WFH' : 'Work From Home'}
        onBack={navigation.goBack}
      />
    ),
    [activeTab],
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.WFH_APPLY}
      fallback={<UnauthorizedScreen />}
    >
      {loading && items.length === 0 && !historyError ? (
        <AppScreen contentStyle={styles.screen} keyboardSafe>
          {header}
          <View style={styles.center}>
            <AppLoader message="Loading work from home" />
          </View>
        </AppScreen>
      ) : (
        <AppScreen contentStyle={styles.screen} keyboardSafe>
          {header}
          {capabilities.canApply ? (
            <UnderlineTabs
              options={WFH_TABS}
              value={activeTab}
              onChange={setActiveTab}
            />
          ) : null}
          {showTeamApprovals ? (
            <View style={styles.approvalAction}>
              <AppButton
                title={wfhApprovalListTitle(auth.roles)}
                onPress={() => navigation.navigate(routes.adminWfhRequests)}
              />
            </View>
          ) : null}

          {activeTab === 'My WFH' || !capabilities.canApply ? (
            <View style={styles.body}>
              {historyError && items.length === 0 ? (
                <ErrorState message={historyError} onRetry={() => load()} />
              ) : (
                <FlatList
                  data={items}
                  keyExtractor={item => String(item.wfhRequestId)}
                  refreshControl={
                    <RefreshControl
                      refreshing={refreshing}
                      onRefresh={() => load(true)}
                      tintColor={palette.primary}
                    />
                  }
                  ListEmptyComponent={
                    <EmptyState
                      title="No WFH requests"
                      message="No WFH requests were found."
                      actionTitle="Retry"
                      onAction={() => load()}
                    />
                  }
                  contentContainerStyle={styles.listContent}
                  renderItem={({ item }) => (
                    <RequestRow
                      item={item}
                      onPress={() => openDetails(item)}
                      palette={palette}
                    />
                  )}
                />
              )}
              {capabilities.canApply ? (
                <View style={styles.footer}>
                  <AppButton
                    title="Apply WFH"
                    onPress={() => setActiveTab('Apply WFH')}
                  />
                </View>
              ) : null}
            </View>
          ) : (
            <ApplyWfhForm
              canApply={capabilities.canApply}
              canApplyForTeam={canApplyForTeam}
              palette={palette}
              onSuccess={appliedForTeamMember => {
                if (appliedForTeamMember) return;
                setActiveTab('My WFH');
                load().catch(() => undefined);
              }}
            />
          )}
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  center: { flex: 1, justifyContent: 'center' },
  body: { flex: 1 },
  approvalAction: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.sm,
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.md },
  row: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.lg,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  rowLeft: { flex: 1, gap: spacing.xs },
  rowRight: { alignItems: 'flex-end', gap: spacing.xs },
  rowTitle: { ...typography.body, fontWeight: '700' },
  rowMeta: { ...typography.caption },
  statusText: { ...typography.body, fontWeight: '700' },
  footer: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.md,
    paddingBottom: spacing.xl,
  },
  applyScreen: {
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xl,
  },
  form: { gap: spacing.lg },
  warning: { ...typography.caption },
  optionRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  optionChip: {
    flex: 1,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  optionChipLabel: { ...typography.body, fontWeight: '600' },
  memberOption: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 12,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    marginTop: spacing.xs,
  },
});
